import { useRestApiProvider } from "@jugl-web/rest-api";
import { BoardTemplate } from "@jugl-web/rest-api/tasks";
import { useCallback, useEffect, useState } from "react";

export const useBoardTemplates = ({
  category,
  searchQuery,
}: {
  category: string;
  searchQuery?: string;
}) => {
  const { tasksApi } = useRestApiProvider();
  const [cat, subcat] = category.split("-");
  const isPopular = cat === "popular";

  // Local pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [moreTemplates, setMoreTemplates] = useState<BoardTemplate[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Query for templates with pagination
  const { data, isLoading, isFetching } = tasksApi.useGetTemplatesListQuery({
    params: {
      type: "board",
      category: isPopular ? undefined : cat,
      sub_category: isPopular ? undefined : subcat,
      search_term: searchQuery ? `%${searchQuery}%` : undefined,
      is_popular: isPopular,
      page: 1,
      page_size: 20,
    },
  });

  // Lazy query for loading more
  const [loadMore] = tasksApi.useLazyGetTemplatesListQuery();

  // Reset pagination when category or search query changes
  useEffect(() => {
    setCurrentPage(1);
    setMoreTemplates([]);
    setHasMore(false);
    setTotalPages(0);
  }, [category, searchQuery]);

  // Update pagination state when data changes
  useEffect(() => {
    if (!data || !(isFetching === false)) {
      return;
    }

    const { total_pages: totalPagesFromApi } = data;

    setTotalPages(totalPagesFromApi || 0);
    setHasMore(currentPage < (totalPagesFromApi || 1));
  }, [data, isFetching, currentPage]);

  // Load more function
  const loadMoreTemplates = useCallback(async () => {
    if (!hasMore || isLoadingMore) {
      return;
    }

    const nextPage = currentPage + 1;

    setIsLoadingMore(true);

    const result = await loadMore({
      params: {
        type: "board",
        category: isPopular ? undefined : cat,
        sub_category: isPopular ? undefined : subcat,
        search_term: searchQuery ? `%${searchQuery}%` : undefined,
        is_popular: isPopular,
        page: nextPage,
        page_size: 20,
      },
    });

    if (result.data) {
      const { data: newTemplatesData, total_pages: totalPagesFromApi } =
        result.data;

      // Append new templates
      setMoreTemplates((prev) => [...prev, ...(newTemplatesData || [])]);
      setCurrentPage(nextPage);
      setHasMore(nextPage < (totalPagesFromApi || 1));
    }
    setIsLoadingMore(false);
  }, [
    hasMore,
    isLoadingMore,
    currentPage,
    loadMore,
    cat,
    subcat,
    isPopular,
    searchQuery,
  ]);

  return {
    templates: [...(data?.data || []), ...moreTemplates],
    totalEntries: data?.total_entries,
    isLoading,
    isLoadingMore,
    hasMore,
    currentPage,
    totalPages,
    loadMore: loadMoreTemplates,
  };
};
